import { Link } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import styles from './Dashboard.module.css'

// Fecha dinámica
function daysUntil(target) {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    return Math.max(0, Math.ceil((target - now) / (1000 * 60 * 60 * 24)))
}

const LAUNCHES = [
    {
        id: 'causacion',
        targetDate: new Date(Date.now() + 15 * 86400000),
        icon: '⚡',
        title: 'Causación Electrónica',
        headline: '¡El futuro del cumplimiento tributario!',
        description: 'Automatización completa de la causación electrónica. Generación automática de archivos, validaciones en tiempo real y cumplimiento DIAN sin esfuerzo.',
        gradient: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ffc93c 100%)',
        glowColor: 'rgba(255, 107, 53, 0.4)',
        badge: '⚡ ¡PRÓXIMAMENTE!',
        badgeGradient: 'linear-gradient(90deg, #ff6b35, #f7931e)',
        isHighlight: true,
    },
]

// Catálogo local de referencia: nombre, icono, descripción y ruta por moduleKey
const MODULOS_INFO = {
    comparador_dian: {
        name: 'Comparador Token DIAN',
        description: 'Compara archivos Token DIAN con libros auxiliares contables',
        icon: '📊',
        path: '/herramientas/comparador-dian',
        color: '#0066CC',
    },
    conciliacion_bancaria: {
        name: 'Conciliación Bancaria',
        description: 'Concilia extractos bancarios con libros auxiliares',
        icon: '🏦',
        path: '/herramientas/conciliacion-bancaria',
        color: '#4A90E2',
    },
    calendario_tributario: {
        name: 'Calendario Tributario',
        description: 'Semáforo de vencimientos DIAN, Mi Planilla y Bogotá 2026 con alertas y asistente IA',
        icon: '🗓️',
        path: '/herramientas/calendario-tributario',
        color: '#1a7c4f',
    },
    tablero_financiero: {
        name: 'Tablero Financiero Ejecutivo',
        description: 'Dashboard P&G, Balance, Indicadores, Riesgos, Comparativo e Informe IA con benchmarks CIIU',
        icon: '📈',
        path: '/herramientas/tablero-financiero',
        color: '#6366f1',
    },
    comisiones: {
        name: 'Gestor de Comisiones',
        description: 'Administración, simulación y liquidación de comisiones de ventas',
        icon: '💰',
        path: '/herramientas/comisiones',
        color: '#f59e0b',
    },
    cuenta_compensacion: {
        name: 'Cuenta de Compensación',
        description: 'Gestión de órdenes de pedido, facturas de comercio exterior y declaraciones de importación (DIM)',
        icon: '🌐',
        path: '/herramientas/cuenta-compensacion',
        color: '#0ea5e9',
    },
    mundo_digital: {
        name: 'Mundo Digital',
        description: 'Tu nuevo ecosistema para acompañar, medir y controlar operaciones con activos digitales',
        icon: '◈',
        path: '/mundo-digital',
        color: '#7c3aed',
    },
    uso_servidores: {
        name: 'Uso de Servidores',
        description: 'Consulta horas encendidas por día, semana y mes y descarga reportes en Excel',
        icon: '🖥️',
        path: '/herramientas/uso-servidores',
        color: '#14b8a6',
    },
}

function Dashboard() {
    const { userName, userEmpresa, userModules, modulesLoaded, refreshUserModules } = useAuth()

    useEffect(() => {
        refreshUserModules()
    }, [refreshUserModules])

    // Construir la lista de herramientas con base en los módulos habilitados del usuario
    const tools = userModules
        .filter(moduleKey => MODULOS_INFO[moduleKey])  // solo los que tienen info en el catálogo local
        .map(moduleKey => ({ id: moduleKey, ...MODULOS_INFO[moduleKey] }))

    return (
        <div className={styles.dashboard}>
            <div className={styles.container}>

                {/* ── Header ───────────────────────────────────────── */}
                <div className={styles.header}>
                    <h1 className={styles.title}>Dashboard</h1>
                    <div className={styles.welcomeSection}>
                        <p className={styles.welcome}>
                            Bienvenido, <strong>{userName}</strong>
                        </p>
                        <p className={styles.empresa}>
                            Empresa: <span className={styles.empresaBadge}>{userEmpresa}</span>
                        </p>
                    </div>
                </div>

                {/* ── Herramientas disponibles ─────────────────────── */}
                <div className={styles.toolsSection}>
                    <h2 className={styles.sectionTitle}>Herramientas Disponibles</h2>

                    {!modulesLoaded ? (
                        <div className={styles.toolsLoading}>Cargando módulos...</div>
                    ) : tools.length === 0 ? (
                        <div className={styles.toolsEmpty}>
                            <span>🔧</span>
                            <p>No tienes módulos habilitados aún. Contacta a tu administrador.</p>
                        </div>
                    ) : (
                        <div className={styles.toolsGrid}>
                            {tools.map((tool) => (
                                <Link
                                    key={tool.id}
                                    to={tool.path}
                                    className={styles.toolCard}
                                    style={{ '--tool-color': tool.color }}
                                >
                                    <div className={styles.toolIcon}>{tool.icon}</div>
                                    <h3 className={styles.toolName}>{tool.name}</h3>
                                    <p className={styles.toolDescription}>{tool.description}</p>
                                    <div className={styles.arrow}>→</div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Próximos lanzamientos ────────────────────────── */}
                <div className={styles.statsSection}>
                    <h2 className={styles.sectionTitle}>🚀 Próximamente en Ingconia</h2>
                    <div className={styles.launchGrid}>
                        {LAUNCHES.map(launch => {
                            const days = daysUntil(launch.targetDate)
                            const pct  = Math.max(8, 100 - days * 9)
                            return (
                                <div
                                    key={launch.id}
                                    className={`${styles.launchCard} ${launch.isHighlight ? styles.highlight : ''}`}
                                    style={{ '--glow': launch.glowColor }}
                                >
                                    {/* Fondo decorativo radial */}
                                    <div className={styles.launchCardBg} style={{ background: launch.gradient }} />

                                    {/* Badge */}
                                    <span className={styles.launchBadge} style={{ backgroundImage: launch.badgeGradient }}>
                                        {launch.badge}
                                    </span>

                                    {/* Cuenta regresiva grande */}
                                    <div className={styles.countdownBlock}>
                                        <div className={styles.countdownRing} style={{ backgroundImage: launch.gradient }}>
                                            <span className={styles.countdownNum}>{days}</span>
                                        </div>
                                        <div className={styles.countdownMeta}>
                                            <span className={styles.countdownDays}>
                                                {days === 1 ? 'DÍA' : 'DÍAS'}
                                            </span>
                                            <span className={styles.countdownSub}>para el lanzamiento</span>
                                        </div>
                                    </div>

                                    {/* Info */}
                                    <div className={styles.launchIcon}>{launch.icon}</div>
                                    <h3 className={styles.launchTitle}>{launch.title}</h3>
                                    <p className={styles.launchHeadline}>{launch.headline}</p>
                                    <p className={styles.launchDescription}>{launch.description}</p>

                                    {/* Barra de progreso */}
                                    <div className={styles.progressTrack}>
                                        <div
                                            className={styles.progressFill}
                                            style={{ backgroundImage: launch.gradient, width: `${pct}%` }}
                                        />
                                    </div>
                                    <p className={styles.progressLabel}>En desarrollo — ¡muy pronto!</p>
                                </div>
                            )
                        })}
                    </div>
                </div>

            </div>
        </div>
    )
}

export default Dashboard
